/* src/pages/SettingsPage.js (Updated) */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

const SettingsPage = () => {
    const navigate = useNavigate();
    const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
    const [user, setUser] = useState(null);
    const [toastMessage, setToastMessage] = useState('');

    // State for the editable profile form
    const [profileData, setProfileData] = useState({ name: '', bio: '' });  
    
    // State for notification preferences
    const [notificationPreferences, setNotificationPreferences] = useState({
        articles: false,
        videos: false,
        reels: false,
        podcasts: false
    });
    
    // --- Book Customization State ---
    const [customization, setCustomization] = useState({
        date_of_birth: '',
        category_preferences: [],
        learning_goals: '',
        motivation_style: '',
        country_of_origin: ''
    });
    const [categories, setCategories] = useState([]);
    const [customizationLoading, setCustomizationLoading] = useState(false);
    const [customizationSaved, setCustomizationSaved] = useState(false);
    
    const location = useLocation(); // Add this line to get the location object

    useEffect(() => {
    if (location.state?.message) {
        setToastMessage(location.state.message);
        // Clean the location state to prevent the message from showing again on refresh
        window.history.replaceState({}, document.title)
    }
    }, [location]);

    // Effect for applying theme
    useEffect(() => {
        document.body.className = '';
        document.body.classList.add(`${theme}-mode`);
        localStorage.setItem('theme', theme);
    }, [theme]);

    // Effect for fetching user data
    useEffect(() => {
        const token = localStorage.getItem('app_token');
        if (token) {
            try {
                const decodedToken = jwtDecode(token);
                setUser(decodedToken);
                
                // Fetch full user profile to get bio
                axios.get(`/api/users/profile/${decodedToken.googleId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                }).then(response => {
                    setProfileData({
                        name: response.data.name,
                        bio: response.data.bio || ''
                    });
                });

                // Fetch notification preferences
                axios.get('/api/notifications/preferences', {
                    headers: { Authorization: `Bearer ${token}` }
                }).then(response => {
                    setNotificationPreferences({
                        articles: !!response.data.articles,
                        videos: !!response.data.videos,
                        reels: !!response.data.reels,
                        podcasts: !!response.data.podcasts
                    });
                }).catch(error => {
                    console.error('Error fetching notification preferences:', error);
                });

            } catch (error) {
                console.error("Invalid token:", error);
                localStorage.removeItem('app_token');
                setUser(null);
            }
        }
    }, []);

    // Fetch categories and customization data
    useEffect(() => {
        const token = localStorage.getItem('app_token');
        if (!token) return;
        setCustomizationLoading(true);
        // Remove Authorization header for categories fetch
        fetch('/api/articles/categories')
            .then(async res => {
                const rawText = await res.text();
                console.log('[DEBUG][FETCH] Raw response text for categories:', rawText);
                let data;
                try {
                    data = JSON.parse(rawText);
                } catch (e) {
                    data = [];
                }
                console.log('[DEBUG][FETCH] Parsed categories:', data, 'Type:', typeof data, 'IsArray:', Array.isArray(data));
                setCategories(Array.isArray(data) ? data : []);
            })
            .catch((err) => {
                console.error('[DEBUG][FETCH] Error fetching categories:', err);
                setCategories([]);
            });
        axios.get('/api/users/book-customization', { headers: { Authorization: `Bearer ${token}` } })
            .then(res => {
                const raw = res.data.category_preferences;
                let parsed = [];
                console.log('[DEBUG] Raw category_preferences from backend:', raw, 'Type:', typeof raw);
                if (Array.isArray(raw)) {
                    parsed = raw;
                } else if (typeof raw === 'string') {
                    try {
                        const tryParse = JSON.parse(raw);
                        if (Array.isArray(tryParse)) {
                            parsed = tryParse;
                        } else if (typeof tryParse === 'string') {
                            parsed = [tryParse];
                        } else {
                            parsed = [];
                        }
                    } catch (e) {
                        // If it's a comma-separated string
                        if (raw.includes(',')) {
                            parsed = raw.split(',').map(s => s.trim());
                        } else if (raw.length > 0) {
                            parsed = [raw];
                        } else {
                            parsed = [];
                        }
                    }
                } else {
                    parsed = [];
                }
                console.log('[DEBUG] Parsed category_preferences for dropdown:', parsed, 'Type:', typeof parsed);
                setCustomization({
                ...customization,
                ...res.data,
                date_of_birth: res.data.date_of_birth ? res.data.date_of_birth.slice(0, 10) : '',
                    category_preferences: parsed
                });
            })
            .catch((err) => {
                console.error('[DEBUG] Error fetching book customization:', err);
            })
            .finally(() => setCustomizationLoading(false));
    }, []);

    const toggleTheme = () => {
        setTheme(prevTheme => (prevTheme === 'light' ? 'dark' : 'light'));
    };

    const handleProfileChange = (e) => {
        setProfileData({ ...profileData, [e.target.name]: e.target.value });
    };

    const handleProfileSave = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('app_token');
        try {
            const response = await axios.put('/api/users/profile', profileData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Update the token in localStorage with the new user info (e.g., new name)
            localStorage.setItem('app_token', response.data.token);
            setToastMessage('Profile saved successfully!');
            setTimeout(() => setToastMessage(''), 3000);

        } catch (error) {
            console.error("Failed to save profile:", error);
            alert('Could not save profile. Please try again.');
        }
    };

    const handleNotificationToggle = async (contentType) => {
        const token = localStorage.getItem('app_token');
        const updatedPreferences = {
            ...notificationPreferences,
            [contentType]: !notificationPreferences[contentType]
        };
        try {
            await axios.put('/api/notifications/preferences', updatedPreferences, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNotificationPreferences(updatedPreferences);
            setToastMessage(`${contentType.charAt(0).toUpperCase() + contentType.slice(1)} notifications ${updatedPreferences[contentType] ? 'enabled' : 'disabled'}!`);
            setTimeout(() => setToastMessage(''), 3000);
        } catch (error) {
            console.error('Failed to update notification preferences:', error);
            setToastMessage('Failed to update notification preferences');
            setTimeout(() => setToastMessage(''), 3000);
        }
    };

    const handleCustomizationChange = (e) => {
        const { name, value, type } = e.target;
        setCustomization(prev => ({
            ...prev,
            [name]: value
        }));
    };
    const handleCategoryChange = (cat) => {
        setCustomization(prev => {
            const arr = Array.isArray(prev.category_preferences) ? prev.category_preferences : [];
            if (arr.includes(cat)) {
                return { ...prev, category_preferences: arr.filter(c => c !== cat) };
            } else {
                return { ...prev, category_preferences: [...arr, cat] };
            }
        });
    };
    const handleCustomizationSave = async (e) => {
        e.preventDefault();
        setCustomizationLoading(true);
        setCustomizationSaved(false);
        const token = localStorage.getItem('app_token');
        try {
            await axios.post('/api/users/book-customization', customization, { headers: { Authorization: `Bearer ${token}` } });
            setCustomizationSaved(true);
            setTimeout(() => setCustomizationSaved(false), 2000);
        } catch (err) {
            alert('Failed to save customization.');
        } finally {
            setCustomizationLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('app_token');
        navigate('/', { state: { message: 'Logout successful!' } });
    };

    const handleDeleteAccount = async () => {
        if (window.confirm('Are you sure you want to permanently delete your account? This action cannot be undone.')) {
            const token = localStorage.getItem('app_token');
            try {
                await axios.delete('/api/users/profile', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                handleLogout(); // Log the user out after deletion
            } catch (error) {
                console.error("Failed to delete account:", error);
                alert('Could not delete your account. Please try again.');
            }
        }
    };

    return (
        <div className="settings-page">
            {toastMessage && <div className="toast-notification">{toastMessage}</div>}
            
            {user && (
                <>
                    {/* Profile Info Card */}
                    <div className="settings-card">
                        <div className="user-info-item">
                            <img src={user.profilePicture} alt={user.name} className="profile-image-small" />
                            <div className="user-details">
                                <span className="user-name">{profileData.name}</span>
                                <span className="user-email">{user.email}</span>
                            </div>
                        </div>
                    </div>

                    {/* Edit Profile Card */}
                    <div className="settings-card">
                         <h3 className="section-title" style={{marginTop: 0}}>Edit Profile</h3>
                         <form onSubmit={handleProfileSave} className="admin-form">
                            <div className="form-group">
                                <label htmlFor="name">Display Name</label>
                                <input type="text" name="name" id="name" className="styled-input" value={profileData.name} onChange={handleProfileChange} />
                            </div>
                            <div className="form-group">
                                <label htmlFor="bio">Bio</label>
                                <textarea name="bio" id="bio" rows="4" className="styled-input" value={profileData.bio} onChange={handleProfileChange} placeholder="Tell us a little about yourself..."></textarea>
                            </div>
                            <button type="submit" className="button-primary" style={{width: 'auto'}}>Save Profile</button>
                         </form>
                    </div>

                    {/* Notification Preferences Card */}
                    <div className="settings-card">
                        <h3 className="section-title" style={{marginTop: 0}}>Email Notifications</h3>
                        <p style={{color: '#666', marginBottom: '20px'}}>
                            Choose which types of content you'd like to receive email notifications for when new content is published.
                        </p>
                        
                        <div className="notification-preferences">
                            <div className="notification-item">
                                <div className="notification-info">
                                    <span className="notification-label">Articles</span>
                                    <span className="notification-description">Get notified when new articles are published</span>
                                </div>
                                <label className="toggle-switch">
                                    <input 
                                        type="checkbox" 
                                        checked={notificationPreferences.articles}
                                        onChange={() => handleNotificationToggle('articles')}
                                    />
                                    <span className="slider round"></span>
                                </label>
                            </div>
                            
                            <div className="notification-item">
                                <div className="notification-info">
                                    <span className="notification-label">Videos</span>
                                    <span className="notification-description">Get notified when new videos are uploaded</span>
                                </div>
                                <label className="toggle-switch">
                                    <input 
                                        type="checkbox" 
                                        checked={notificationPreferences.videos}
                                        onChange={() => handleNotificationToggle('videos')}
                                    />
                                    <span className="slider round"></span>
                                </label>
                            </div>
                            
                            <div className="notification-item">
                                <div className="notification-info">
                                    <span className="notification-label">Reels</span>
                                    <span className="notification-description">Get notified when new reels are posted</span>
                                </div>
                                <label className="toggle-switch">
                                    <input 
                                        type="checkbox" 
                                        checked={notificationPreferences.reels}
                                        onChange={() => handleNotificationToggle('reels')}
                                    />
                                    <span className="slider round"></span>
                                </label>
                            </div>
                            
                            <div className="notification-item">
                                <div className="notification-info">
                                    <span className="notification-label">Podcasts</span>
                                    <span className="notification-description">Get notified when new podcasts are released</span>
                                </div>
                                <label className="toggle-switch">
                                    <input 
                                        type="checkbox" 
                                        checked={notificationPreferences.podcasts}
                                        onChange={() => handleNotificationToggle('podcasts')}
                                    />
                                    <span className="slider round"></span>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* --- Book Customization Card --- */}
                    <div className="settings-card">
                        <h3 className="section-title" style={{marginTop: 0}}>Book Customization</h3>
                        <form onSubmit={handleCustomizationSave} className="admin-form">
                            <div className="form-group">
                                <label htmlFor="date_of_birth">Date of Birth</label>
                                <input type="date" name="date_of_birth" id="date_of_birth" className="styled-input" value={customization.date_of_birth} onChange={handleCustomizationChange} />
                            </div>
                            <div className="form-group">
                                <label htmlFor="category_preferences">Article Categories to Improve</label>
                                <select
                                    id="category_preferences"
                                    name="category_preferences"
                                    className="styled-input"
                                    multiple
                                    value={customization.category_preferences}
                                    onChange={e => {
                                        const selected = Array.from(e.target.selectedOptions, option => option.value);
                                        setCustomization(prev => ({ ...prev, category_preferences: selected }));
                                    }}
                                    style={{ minHeight: 80, width: '100%', padding: 8 }}
                                >
                                    {categories.map(cat => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>
                                <small>Select one or more categories to help us improve your book experience.</small>
                                {customization.category_preferences.length > 0 && (
                                    <div style={{ marginTop: 8 }}>
                                        <strong>Selected:</strong> {customization.category_preferences.join(', ')}
                                    </div>
                                )}
                            </div>
                            <div className="form-group">
                                <label htmlFor="learning_goals">Learning Goals</label>
                                <textarea name="learning_goals" id="learning_goals" rows="2" className="styled-input" value={customization.learning_goals} onChange={handleCustomizationChange} placeholder="What do you hope to achieve?" />
                            </div>
                            <div className="form-group">
                                <label htmlFor="motivation_style">Motivation Style</label>
                                <textarea name="motivation_style" id="motivation_style" rows="2" className="styled-input" value={customization.motivation_style} onChange={handleCustomizationChange} placeholder="Describe your preferred motivation style..." />
                            </div>
                            <div className="form-group">
                                <label htmlFor="country_of_origin">Country of Origin</label>
                                <input type="text" name="country_of_origin" id="country_of_origin" className="styled-input" value={customization.country_of_origin} onChange={handleCustomizationChange} placeholder="Your country..." />
                            </div>
                            <button type="submit" className="button-primary" style={{width: 'auto'}} disabled={customizationLoading}>{customizationLoading ? 'Saving...' : 'Save Customization'}</button>
                            {customizationSaved && <span style={{ color: 'green', marginLeft: 12 }}>Saved!</span>}
                        </form>
                    </div>
                </>
            )}

            {/* Appearance Card */}
            <div className="settings-card">
                <div className="setting-item">
                    <span className="setting-label">Appearance</span>
                    <div className="theme-toggle-container">
                        <span className={`theme-icon material-icons ${theme === 'light' ? 'active' : ''}`}>light_mode</span>
                        <label className="theme-toggle-switch">
                            <input type="checkbox" onChange={toggleTheme} checked={theme === 'dark'} />
                            <span className="slider round"></span>
                        </label>
                        <span className={`theme-icon material-icons ${theme === 'dark' ? 'active' : ''}`}>dark_mode</span>
                    </div>
                </div>
            </div>
            
            {/* Account Management Card */}
            {user && (
                 <div className="settings-card">
                     <h3 className="section-title" style={{marginTop: 0}}>Account Management</h3>
                     <button onClick={handleLogout} className="button-primary">Logout</button>
                     <button onClick={handleDeleteAccount} className="button-danger" style={{marginTop: '1rem'}}>Delete Account</button>
                </div>
            )}
        </div>
    );
};

export default SettingsPage;
